import { UploadOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, Modal, Select, Upload } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import React, { FC, useEffect } from 'react';

import { dateFormat } from '../../../../common/constants';
import { openNotification } from '../../../../common/helpers';
import { NotificationTypes } from '../../../../common/interfaces';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';
import { UploadDocument } from '../../../../store/api/types.api';
import { DocUploadedUser } from '../../Registration.interfaces';
import styles from './ModalUpload.module.scss';

type Props = {
  loadingSend: boolean;
  activeDocument: DocUploadedUser | null;
  isOpen: boolean;
  onClose: () => void;
  name?: string;
  onSubmit: (data: UploadDocument) => Promise<void>;
  onDeleteFile: (fileId: string) => void;
};

export const ModalUpload: FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  loadingSend,
}) => {
  const [documentBase64, setDocumentBase64] = React.useState<{
    extension: string;
    data: string;
  } | null>(null);

  const [form] = Form.useForm();

  const { requiredDocSelect } = useTypedSelector((state) => state.registration);

  const closeModal = () => {
    setDocumentBase64(null);
    form.resetFields();
    onClose();
  };

  // Функция подготавливает данные для отправки
  const beforeUpload = (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64StringMIME = reader.result as string;
      const base64String = base64StringMIME.split(',')[1];

      setDocumentBase64({
        extension: file.name.split('.').pop() as string,
        data: base64String,
      });
    };
    return false; // prevent the file from being uploaded
  };

  const handleUpload = async (values: UploadDocument) => {
    const fileName = requiredDocSelect.find(
      (item) => item.value === values.doctypeId,
    )?.label;

    if (documentBase64) {
      const requestObjFile: UploadDocument = {
        fileName:
          (values.fileName ?? fileName) + '.' + documentBase64.extension,
        number: values.number,
        date: (dayjs(values.date).format(dateFormat) as string) ?? '',
        validTo: (dayjs(values.validTo).format(dateFormat) as string) ?? '',
        comment: values.comment ?? '',
        doctypeId: values.doctypeId,
        data: documentBase64.data,
        validFrom: (dayjs(values.validFrom).format(dateFormat) as string) ?? '',
      };

      try {
        await onSubmit(requestObjFile);
        openNotification(
          NotificationTypes.SUCCESS,
          'Документ успешно загружен на сервер',
        );
        closeModal();
      } catch (e) {
        console.error(e);
        openNotification(
          NotificationTypes.ERROR,
          `Произошла ошибка при загрузке документа. Попробуйте еще раз.`,
        );
      }
    }
  };

  return (
    <Modal
      title="Загрузка документа"
      width={600}
      onCancel={onClose}
      open={isOpen}
      footer={null}
    >
      <div className={styles.container}>
        <Form
          onFinish={handleUpload}
          className={styles.form}
          layout="vertical"
          form={form}
        >
          <div className={styles.details}>
            <Form.Item
              rules={[{ required: true, message: 'Обязательное поле' }]}
              className={styles.inputs}
              label="Тип документа"
              name="doctypeId"
            >
              <Select options={requiredDocSelect} placeholder="Тип документа" />
            </Form.Item>

            <Form.Item
              // rules={[{ required: true, message: 'Обязательное поле' }]}
              className={styles.inputs}
              label="Наименование документа (на русском)"
              name="fileName"
            >
              <Input placeholder="Наименование документа (на русском)" />
            </Form.Item>

            <Form.Item
              // rules={[{ required: true, message: 'Обязательное поле' }]}
              className={styles.inputs}
              label="Номер документа"
              name="number"
            >
              <Input placeholder="Номер документа" />
            </Form.Item>

            <Form.Item
              rules={[{ required: true, message: 'Обязательное поле' }]}
              className={styles.inputs}
              label="Дата документа"
              name="date"
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Дата документа"
              />
            </Form.Item>

            <Form.Item
              rules={[{ required: true, message: 'Обязательное поле' }]}
              className={styles.inputs}
              label="Дата начала действия документа"
              name="validFrom"
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Дата начала действия"
              />
            </Form.Item>

            {/* <Form.Item>
            <DatePicker placeholder="Срок действия с ..." />
          </Form.Item> */}

            <Form.Item
              // rules={[{ required: true, message: 'Обязательное поле' }]}
              className={styles.inputs}
              label="Дата окончания действия документа"
              name="validTo"
              extra="Если не указана, то бессрочно"
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Срок действия по ..."
              />
            </Form.Item>

            <Form.Item
              className={styles.inputs}
              label="Дата нотариального заверения (при наличии)"
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Дата нотариального заверения"
              />
            </Form.Item>

            <Form.Item
              className={styles.inputs}
              label="Комментарий"
              name="comment"
            >
              <TextArea placeholder="Комментарий" />
            </Form.Item>
          </div>

          <div className={styles.upload}>
            <Upload
              // запрещаем отправлять файл автоматически (antd)
              beforeUpload={beforeUpload}
              multiple={false}
              maxCount={1}
              onRemove={() => setDocumentBase64(null)}
            >
              <Button
                block
                size="large"
                type="dashed"
                variant="outlined"
                icon={<UploadOutlined />}
              >
                Загрузить документ
              </Button>

              {!documentBase64 && (
                <div className={styles['error-load-doc']}>
                  Загрузите документ!
                </div>
              )}
            </Upload>
          </div>

          <div className={styles['buttons-group']}>
            <Button onClick={closeModal} htmlType="button">
              Отмена
            </Button>

            <Button
              onKeyDown={(e) => (e.key === 'Enter' ? e.preventDefault() : '')}
              type="primary"
              htmlType="submit"
              loading={loadingSend}
            >
              Отправить
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};
